import React, { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loadUsers, logAccess } from "../services/storageService";
import { loginRequest } from "../authConfig";

const AuthContext = createContext(null);

// Sessao expira automaticamente 8h depois do primeiro login.
// O instante e gravado em sessionStorage no primeiro render com conta valida.
// Refresh da pagina nao reinicia o relogio (sessionStorage sobrevive ao reload
// dentro da mesma aba). Fechar a aba zera tudo e na proxima abertura recomeca.
const SESSION_MAX_MS = 8 * 60 * 60 * 1000; // 8 horas
const SESSION_LOGIN_KEY = "ptp_login_at";

// Cargos / departamentos do Azure AD que recebem perfil admin automaticamente
const ADMIN_TITLES = [
  /head/i,
  /team\s*lead/i,
  /tech\s*lead/i,
  /l[íi]der/i,                     // Líder, Lider
  /coordena/i,                     // Coordenador, Coordenadora
  /gerente/i,
  /manager/i,
  /pessoas\s*e\s*cultura/i,        // Equipe de P&C
  /people\s*(and|&)\s*culture/i,
  /\bp\s*&\s*c\b/i,                // P&C
  /recursos\s*humanos/i,
];

// Se tanto o blob (users.json) quanto o Graph falharem, o usuário cai em "usuario".
// Para recuperar acesso admin em emergência (ex.: users.json corrompido):
//   Portal Azure -> Storage account "intranetpowertuning" -> Containers -> "sites"
//   -> editar/upload de users.json com { "users": [{ "email": "...", "role": "admin" }] }.
// Evitamos lista hardcoded aqui porque ela vira backdoor silencioso no bundle JS.

export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const [role, setRole] = useState(null); // "admin" | "lider" | "usuario" | null
  const [loading, setLoading] = useState(true);

  // ── Resolve perfil + registra acesso no primeiro login ──
  useEffect(() => {
    if (!accounts.length) { setLoading(false); return; }
    const account = accounts[0];
    resolveRole(instance, account).then((r) => {
      setRole(r);
      setLoading(false);
      // Registra o último acesso (não bloqueia o fluxo se falhar)
      logAccess({ email: account.username, name: account.name }).catch(() => {});
    });
  }, [accounts, instance]);

  // ── Auto-logout apos 8h do primeiro login da sessao ──
  useEffect(() => {
    if (!accounts.length) return;

    let loginAt = parseInt(sessionStorage.getItem(SESSION_LOGIN_KEY) || "", 10);
    if (!loginAt || Number.isNaN(loginAt)) {
      loginAt = Date.now();
      sessionStorage.setItem(SESSION_LOGIN_KEY, String(loginAt));
    }

    const remaining = loginAt + SESSION_MAX_MS - Date.now();
    if (remaining <= 0) {
      forceLogout(instance);
      return;
    }

    const timer = setTimeout(() => forceLogout(instance), remaining);
    return () => clearTimeout(timer);
  }, [accounts, instance]);

  return (
    <AuthContext.Provider value={{ role, loading, account: accounts[0] ?? null }}>
      {children}
    </AuthContext.Provider>
  );
}

function forceLogout(instance) {
  try {
    sessionStorage.removeItem(SESSION_LOGIN_KEY);
    // Mensagem rapida pro usuario entender o motivo do redirect
    window.alert("Sua sessão expirou (8 horas). Faça login novamente.");
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  } catch (err) {
    console.warn("Auto-logout falhou, recarregando a pagina:", err);
    window.location.reload();
  }
}

export function useAuth() { return useContext(AuthContext); }

async function resolveRole(instance, account) {
  const email = (account.username || "").toLowerCase();

  // 1. Override explícito via users.json
  try {
    const data = await loadUsers();
    const match = (data.users || []).find(u => u.email?.toLowerCase() === email);
    if (match?.role) return match.role;
  } catch {
    // segue para o próximo passo
  }

  // 2. Cargo do Azure AD via Microsoft Graph
  try {
    const tokenRes = await instance.acquireTokenSilent({ ...loginRequest, account });
    const select = "jobTitle,department,officeLocation,employeeType,onPremisesExtensionAttributes";
    const res = await fetch(`https://graph.microsoft.com/v1.0/me?$select=${select}`, {
      headers: { Authorization: `Bearer ${tokenRes.accessToken}` },
    });
    if (res.ok) {
      const data = await res.json();
      const ext = data.onPremisesExtensionAttributes || {};
      const candidates = [
        data.jobTitle,
        data.department,
        data.officeLocation,
        data.employeeType,
        ...Object.values(ext),
      ].filter(Boolean).map(String);
      if (candidates.some(c => ADMIN_TITLES.some(re => re.test(c)))) return "admin";
    }
  } catch {
    // segue para o fallback
  }

  // 3. Default — sem fallback hardcoded (ver comentário no topo do arquivo)
  return "usuario";
}
